interface Options {
  position?: number;
  distance?: number;
  offset?: number;
  restrictionMode?: string;
  restrictionTarget?: string;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
}
export interface TooltipOwner {
  invokeMethodAsync(method: string, open: boolean, reason: string | null): Promise<unknown>;
}
interface Entry {
  tooltip: HTMLElement;
  target: HTMLElement;
  owner: TooltipOwner | null;
  options: Options;
  open: boolean;
  pinned: boolean;
  openedBy: string | null;
  listeners: { mouseenter: () => void; mouseleave: () => void; click: () => void;
    keydown: (event: KeyboardEvent) => void; focusin: () => void;
    focusout: (event: FocusEvent) => void } | null;
}
interface Rect { x: number; y: number; w: number; h: number }

const TOLERANCE = 0.01;

const Position = {
  TopStart: 1,
  Top: 2,
  TopEnd: 4,
  RightStart: 8,
  Right: 16,
  RightEnd: 32,
  BottomStart: 64,
  Bottom: 128,
  BottomEnd: 256,
  LeftStart: 512,
  Left: 1024,
  LeftEnd: 2048
};

const AllPositions =
  Position.TopStart | Position.Top | Position.TopEnd |
  Position.RightStart | Position.Right | Position.RightEnd |
  Position.BottomStart | Position.Bottom | Position.BottomEnd |
  Position.LeftStart | Position.Left | Position.LeftEnd;

const entries = new Map<HTMLElement, Entry>();
let listening = false;
let repositionScheduled = false;

export function register(tooltip: HTMLElement, target: HTMLElement, options: Options, owner: TooltipOwner) {
  if (!tooltip || !target) {
    return;
  }

  let entry = entries.get(tooltip);
  if (!entry) {
    entry = {
      tooltip,
      target,
      owner: null,
      options: {},
      open: false,
      pinned: false,
      openedBy: null,
      listeners: null
    };
    entries.set(tooltip, entry);
  }

  if (entry.target !== target) detachTrigger(entry);
  entry.target = target;
  entry.owner = owner || null;
  entry.options = options || {};
  attachTrigger(entry);
  ensureListeners();
}

export function unregister(tooltip: HTMLElement) {
  const entry = entries.get(tooltip);
  if (!entry) {
    return;
  }

  closeEntry(entry, 'disconnect', false);
  detachTrigger(entry);
  entries.delete(tooltip);
  stopListenersIfIdle();
}

export function show(tooltip: HTMLElement, target: HTMLElement, options: Options) {
  if (!tooltip || !target) {
    return;
  }

  let entry = entries.get(tooltip);
  if (!entry) {
    entry = {
      tooltip,
      target,
      owner: null,
      options: {},
      open: false,
      pinned: false,
      openedBy: null,
      listeners: null
    };
    entries.set(tooltip, entry);
    attachTrigger(entry);
    ensureListeners();
  }

  entry.options = { ...entry.options, ...options };
  openEntry(entry, 'api', false);
}

export function hide(tooltip: HTMLElement) {
  const entry = entries.get(tooltip);
  if (!entry) {
    if (tooltip) {
      tooltip.classList.remove('lib-tooltip-visible');
    }

    return;
  }

  closeEntry(entry, 'api', false);
}

function openEntry(entry: Entry, source: string, notify: boolean) {
  for (const other of entries.values()) {
    if (other !== entry && other.open) {
      closeEntry(other, 'exclusive', true);
    }
  }

  if (entry.open) {
    positionEntry(entry.tooltip, entry);
  } else {
    entry.open = true;
    entry.openedBy = source;
    if (source === 'click') {
      entry.pinned = true;
    }

    positionEntry(entry.tooltip, entry);

  }

  if (notify) {
    notifyOwner(entry, true, source);
  }
}

function closeEntry(entry: Entry, reason: string, notify: boolean) {
  const wasOpen = entry.open;
  entry.open = false;
  entry.pinned = false;
  entry.openedBy = null;
  entry.tooltip.classList.remove('lib-tooltip-visible');

  if (notify && wasOpen) {
    notifyOwner(entry, false, reason);
  }
}

async function notifyOwner(entry: Entry, isOpen: boolean, reason: string) {
  if (!entry.owner) {
    return;
  }

  try {
    await entry.owner.invokeMethodAsync('OnTooltipInteraction', isOpen, reason || null);
  } catch {
    return;
  }
}

function attachTrigger(entry: Entry) {
  if (entry.listeners || !entry.target) {
    return;
  }

  if (typeof entry.target.addEventListener !== 'function') {
    return;
  }

  const toggle = () => {
    if (!entry.open) {
      openEntry(entry, 'click', true);
    } else if (!entry.pinned) {
      entry.pinned = true;
    } else {
      closeEntry(entry, 'toggle', true);
    }
  };

  const listeners = {
    mouseenter: () => {
      if (hasHover() && !entry.open) {
        openEntry(entry, 'hover', true);
      }
    },
    mouseleave: () => {
      if (hasHover() && entry.open && !entry.pinned
        && (entry.openedBy === 'hover' || entry.openedBy === 'focus')) {
        closeEntry(entry, 'hover-out', true);
      }
    },
    click: toggle,
    keydown: (event: KeyboardEvent) => {
      if (entry.target.matches('button, input, select, textarea, a[href], lib-button')) return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        toggle();
      }
    },
    focusin: () => {
      if (!entry.open) {
        openEntry(entry, 'focus', true);
      }
    },
    focusout: (event: FocusEvent) => {
      const next = event.relatedTarget instanceof Node ? event.relatedTarget : null;
      if (next && (entry.tooltip.contains(next) || next === entry.target)) {
        return;
      }

      if (entry.open && !entry.pinned) {
        closeEntry(entry, 'blur', true);
      }
    }
  };

  entry.listeners = listeners;
  entry.target.addEventListener('mouseenter', listeners.mouseenter);
  entry.target.addEventListener('mouseleave', listeners.mouseleave);
  entry.target.addEventListener('click', listeners.click);
  entry.target.addEventListener('keydown', listeners.keydown);
  entry.target.addEventListener('focusin', listeners.focusin);
  entry.target.addEventListener('focusout', listeners.focusout);
}

function detachTrigger(entry: Entry) {
  const listeners = entry.listeners;
  entry.listeners = null;
  if (!listeners || !entry.target) {
    return;
  }

  entry.target.removeEventListener('mouseenter', listeners.mouseenter);
  entry.target.removeEventListener('mouseleave', listeners.mouseleave);
  entry.target.removeEventListener('click', listeners.click);
  entry.target.removeEventListener('keydown', listeners.keydown);
  entry.target.removeEventListener('focusin', listeners.focusin);
  entry.target.removeEventListener('focusout', listeners.focusout);
}

function hasHover() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function ensureListeners() {
  if (listening) {
    return;
  }

  document.addEventListener('scroll', scheduleReposition, true);
  window.addEventListener('resize', scheduleReposition);
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('keydown', onDocumentKeyDown);
  listening = true;
}

function stopListenersIfIdle() {
  if (!listening || entries.size > 0) {
    return;
  }

  document.removeEventListener('scroll', scheduleReposition, true);
  window.removeEventListener('resize', scheduleReposition);
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  document.removeEventListener('keydown', onDocumentKeyDown);
  listening = false;
}

function onDocumentPointerDown(event: PointerEvent) {
  for (const entry of entries.values()) {
    if (!entry.open || entry.options.closeOnOutsideClick === false) {
      continue;
    }

    const target = entry.target;
    if (event.composedPath().includes(entry.tooltip)
      || (target && (target === event.target || target.contains(event.target as Node)))) {
      continue;
    }

    closeEntry(entry, 'outside', true);
  }
}

function onDocumentKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape') {
    return;
  }

  for (const entry of entries.values()) {
    if (entry.open && entry.options.closeOnEscape !== false) {
      closeEntry(entry, 'escape', true);
    }
  }
}

function scheduleReposition() {
  if (repositionScheduled) {
    return;
  }

  repositionScheduled = true;
  requestAnimationFrame(() => {
    repositionScheduled = false;
    for (const [tooltip, entry] of entries) {
      if (!tooltip.isConnected) {
        detachTrigger(entry);
        entries.delete(tooltip);
        continue;
      }

      if (entry.open) {
        positionEntry(tooltip, entry);
      }
    }

    stopListenersIfIdle();
  });
}

function positionEntry(tooltip: HTMLElement, entry: Entry) {
  const target = toPageRect(entry.target);
  if (!target) {
    return;
  }

  const options = entry.options;
  const restriction = getRestrictionRect(options.restrictionMode, options.restrictionTarget);
  const width = tooltip.offsetWidth;
  const height = tooltip.offsetHeight;
  if (width <= 0 || height <= 0) {
    return;
  }

  const distance = typeof options.distance === 'number' ? options.distance : 9;
  const offset = typeof options.offset === 'number' ? options.offset : 0;

  const flags = !options.position ? AllPositions : options.position;
  const candidates = createCandidates(flags, target, width, height, distance, offset);

  let best = null;
  let bestArea = -1;
  for (const candidate of candidates) {
    const area = overlapArea(
      restriction,
      target.x + candidate.x,
      target.y + candidate.y,
      width,
      height);

    if (area - bestArea > TOLERANCE) {
      best = candidate;
      bestArea = area;
    }

    if (Math.abs(area - width * height) < TOLERANCE) {
      break;
    }
  }

  if (!best) {
    return;
  }

  const dialog = clampToRestriction(
    { x: target.x + best.x, y: target.y + best.y, w: width, h: height },
    restriction);

  if (!isTargetWithinRestriction(target, restriction)) {
    tooltip.classList.remove('lib-tooltip-visible');
    return;
  }

  tooltip.classList.add('lib-tooltip-visible');
  tooltip.style.left = `${Math.round(dialog.x) - window.scrollX}px`;
  tooltip.style.top = `${Math.round(dialog.y) - window.scrollY}px`;
  tooltip.dataset.libSide = best.side;
}

function createCandidates(position: number, target: Rect, width: number, height: number, distance: number, offset: number) {
  const list = [];
  const centeredX = (target.w - width) / 2 + offset;
  const centeredY = (target.h - height) / 2 + offset;
  const bottomY = target.h + distance;
  const topY = -(height + distance);
  const leftX = -(width + distance);
  const rightX = target.w + distance;

  if (position & Position.Bottom) {
    list.push({ side: 'bottom', flag: Position.Bottom, x: centeredX, y: bottomY });
  }

  if (position & Position.BottomStart) {
    list.push({ side: 'bottom', flag: Position.BottomStart, x: offset, y: bottomY });
  }

  if (position & Position.BottomEnd) {
    list.push({ side: 'bottom', flag: Position.BottomEnd, x: target.w - width + offset, y: bottomY });
  }

  if (position & Position.Top) {
    list.push({ side: 'top', flag: Position.Top, x: centeredX, y: topY });
  }

  if (position & Position.TopStart) {
    list.push({ side: 'top', flag: Position.TopStart, x: offset, y: topY });
  }

  if (position & Position.TopEnd) {
    list.push({ side: 'top', flag: Position.TopEnd, x: target.w - width + offset, y: topY });
  }

  if (position & Position.Left) {
    list.push({ side: 'start', flag: Position.Left, x: leftX, y: centeredY });
  }

  if (position & Position.LeftStart) {
    list.push({ side: 'start', flag: Position.LeftStart, x: leftX, y: offset });
  }

  if (position & Position.LeftEnd) {
    list.push({ side: 'start', flag: Position.LeftEnd, x: leftX, y: target.h - height + offset });
  }

  if (position & Position.Right) {
    list.push({ side: 'end', flag: Position.Right, x: rightX, y: centeredY });
  }

  if (position & Position.RightStart) {
    list.push({ side: 'end', flag: Position.RightStart, x: rightX, y: offset });
  }

  if (position & Position.RightEnd) {
    list.push({ side: 'end', flag: Position.RightEnd, x: rightX, y: target.h - height + offset });
  }

  return list;
}

function clampToRestriction(dialog: Rect, restriction: Rect) {
  if (dialog.x <= restriction.x) {
    dialog.x = restriction.x;
  } else if (dialog.x + dialog.w > restriction.x + restriction.w) {
    dialog.x = restriction.x + restriction.w - dialog.w;
  }

  if (dialog.x < 0) {
    dialog.x = 0;
  }

  if (dialog.y + dialog.h > restriction.y + restriction.h) {
    dialog.y = restriction.y + restriction.h - dialog.h;
  } else if (dialog.y < restriction.y) {
    dialog.y = restriction.y;
  }

  return dialog;
}

function isTargetWithinRestriction(target: Rect, restriction: Rect) {
  const corners = [
    [target.x, target.y],
    [target.x + target.w, target.y],
    [target.x, target.y + target.h],
    [target.x + target.w, target.y + target.h]
  ];

  return corners.some(([x, y]) =>
    x >= restriction.x &&
    x <= restriction.x + restriction.w &&
    y >= restriction.y &&
    y <= restriction.y + restriction.h);
}

function overlapArea(restriction: Rect, x: number, y: number, width: number, height: number) {
  const left = Math.max(restriction.x, x);
  const top = Math.max(restriction.y, y);
  const right = Math.min(restriction.x + restriction.w, x + width);
  const bottom = Math.min(restriction.y + restriction.h, y + height);
  return right <= left || bottom <= top ? 0 : (right - left) * (bottom - top);
}

function getRestrictionRect(mode?: string, selector?: string) {
  switch (mode) {
    case 'viewport':
      return {
        x: window.scrollX,
        y: window.scrollY,
        w: document.documentElement.clientWidth,
        h: document.documentElement.clientHeight
      };
    case 'targetelement': {
      const element = selector ? document.querySelector(selector) : null;
      if (element) {
        return toPageRect(element);
      }

      break;
    }
  }

  return {
    x: 0,
    y: 0,
    w: document.body.clientWidth,
    h: document.body.clientHeight
  };
}

function toPageRect(element: Element) {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + window.scrollX,
    y: rect.top + window.scrollY,
    w: rect.width,
    h: rect.height
  };
}
